import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, useTheme, Grid, FormControl, Select, MenuItem,
  IconButton, Chip, CircularProgress, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButton, ToggleButtonGroup, InputLabel, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CorrectIcon,
  Cancel as IncorrectIcon,
  Adjust as PartialIcon,
  Flag as FlagIcon,
  HowToVote as VerdictIcon,
  OpenInNew as OpenIcon,
  EmojiEvents as GoldenSetIcon,
  Schedule as ClockIcon,
  Person as PersonIcon,
  PlaylistAddCheck as SuggestionsIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Analytics as AnalyzeIcon,
} from '@mui/icons-material';
import {
  RootState,
  HITLReviewItem,
  HITLReviewQueueResponse,
  HITLReviewStats,
  GoldenSetCandidate,
  TemplateStepSuggestion,
} from '../types';
import { fetchClients } from '../store/slices/clientsSlice';
import { PlaybooksService } from '../services/playbooksService';
import { getRelativeTimeString, formatDate } from '../utils/dateUtils';
import { DataTable, Column } from '../components/common';
import { colorUtils } from '../theme';

// ── Correction reasons ────────────────────────────────────────────────────────

const CORRECTION_REASONS = [
  { value: 'wrong_confidence',  label: 'Wrong confidence level' },
  { value: 'missing_context',   label: 'Missing context / data' },
  { value: 'wrong_technique',   label: 'Wrong MITRE technique' },
  { value: 'wrong_scope',       label: 'Wrong scope / affected systems' },
  { value: 'hallucinated_ioc',  label: 'Hallucinated IOC or entity' },
  { value: 'wrong_next_steps',  label: 'Wrong suggested next steps' },
  { value: 'other',             label: 'Other' },
] as const;

type CorrectionReason = typeof CORRECTION_REASONS[number]['value'];

// ── Verdict dialog (two-step: verdict → optional golden set) ─────────────────

interface VerdictDialogProps {
  item: HITLReviewItem | null;
  clientId: string;
  onClose: () => void;
  onSubmit: (verdict: 'correct' | 'incorrect' | 'partial', notes: string) => Promise<void>;
  onGoldenSetAdded: () => void;
}

const VerdictDialog: React.FC<VerdictDialogProps> = ({ item, clientId, onClose, onSubmit, onGoldenSetAdded }) => {
  const theme = useTheme();

  // Step 1: verdict form
  const [verdict, setVerdict]       = useState<'correct' | 'incorrect' | 'partial' | ''>('');
  const [reason, setReason]         = useState<CorrectionReason | ''>('');
  const [notes, setNotes]           = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Step 2: golden set prompt
  const [step, setStep]             = useState<'verdict' | 'golden'>('verdict');
  const [goldenName, setGoldenName] = useState('');
  const [addingGolden, setAddingGolden] = useState(false);
  const [goldenError, setGoldenError]   = useState<string | null>(null);

  // Reset when item changes
  useEffect(() => {
    setVerdict(''); setReason(''); setNotes('');
    setStep('verdict'); setGoldenName(''); setGoldenError(null);
  }, [item?.session.id]);

  const needsReason = verdict === 'incorrect' || verdict === 'partial';

  const verdictCfg = {
    correct:   { label: 'Correct',   color: theme.palette.success.main,  icon: <CorrectIcon   sx={{ fontSize: 18 }} /> },
    incorrect: { label: 'Incorrect', color: theme.palette.error.main,    icon: <IncorrectIcon sx={{ fontSize: 18 }} /> },
    partial:   { label: 'Partial',   color: theme.palette.warning.main,  icon: <PartialIcon   sx={{ fontSize: 18 }} /> },
  } as const;

  const handleSubmitVerdict = async () => {
    if (!verdict) return;
    setSubmitting(true);
    const fullNotes = needsReason && reason
      ? `[REASON:${reason}]${notes ? ' ' + notes : ''}`
      : notes;
    try {
      await onSubmit(verdict, fullNotes);
      // Move to golden set step instead of closing
      setGoldenName(item?.ticket?.title ?? `Session ${item?.session.id.slice(0, 8)}`);
      setStep('golden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddToGoldenSet = async () => {
    if (!item) return;
    setAddingGolden(true);
    setGoldenError(null);
    try {
      await PlaybooksService.createGoldenSetEntry({
        client_id: clientId,
        session_id: item.session.id,
        name: goldenName.trim() || undefined,
      });
      onGoldenSetAdded();
      onClose();
    } catch (e: any) {
      setGoldenError(e.message ?? 'Failed to add to golden set');
    } finally {
      setAddingGolden(false);
    }
  };

  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}
    >
      {step === 'verdict' ? (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Submit Analyst Verdict</Typography>
            {item && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.25 }}>
                Session {item.session.id.slice(0, 8)}…{item.ticket?.title && ` · ${item.ticket.title}`}
              </Typography>
            )}
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: '0.05em', display: 'block', mb: 1.5 }}>
              WAS THE AI ANALYSIS CORRECT?
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
              {(['correct', 'incorrect', 'partial'] as const).map((v) => {
                const cfg = verdictCfg[v];
                const selected = verdict === v;
                return (
                  <Box key={v} onClick={() => { setVerdict(v); if (v === 'correct') setReason(''); }}
                    sx={{
                      flex: 1, minWidth: 110, p: 1.5, borderRadius: 1.5,
                      border: `2px solid ${selected ? cfg.color : theme.palette.divider}`,
                      backgroundColor: selected ? colorUtils.withOpacity(cfg.color, 0.08) : 'transparent',
                      cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: cfg.color, backgroundColor: colorUtils.withOpacity(cfg.color, 0.05) },
                    }}>
                    <Box sx={{ color: selected ? cfg.color : theme.palette.text.secondary }}>{cfg.icon}</Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 500, color: selected ? cfg.color : theme.palette.text.secondary }}>
                      {cfg.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {needsReason && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>What was wrong? (required)</InputLabel>
                <Select value={reason} label="What was wrong? (required)"
                  onChange={(e) => setReason(e.target.value as CorrectionReason)}
                  sx={{ fontSize: '0.8rem', backgroundColor: theme.palette.background.tertiary }}>
                  {CORRECTION_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value} sx={{ fontSize: '0.8rem' }}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label={needsReason ? 'Additional notes (optional)' : 'Notes (optional)'}
              multiline minRows={2} fullWidth value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What specifically was correct or incorrect about the AI output?"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, '& fieldset': { borderColor: theme.palette.divider } } }}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>Cancel</Button>
            <Button variant="contained" size="small"
              disabled={!verdict || (needsReason && !reason) || submitting}
              onClick={handleSubmitVerdict}
              startIcon={submitting ? <CircularProgress size={13} color="inherit" /> : <VerdictIcon sx={{ fontSize: 15 }} />}
              sx={{ fontWeight: 600, px: 2 }}>
              {submitting ? 'Submitting…' : 'Submit verdict'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CorrectIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Verdict submitted</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
              Would you like to add this session to the Golden Set as a training benchmark?
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <TextField fullWidth size="small" label="Entry name"
              value={goldenName} onChange={(e) => setGoldenName(e.target.value)}
              InputProps={{ sx: { fontSize: '0.82rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
            />
            {goldenError && (
              <Typography variant="caption" sx={{ color: theme.palette.error.main, display: 'block', mt: 1, fontSize: '0.72rem' }}>
                {goldenError}
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
              Skip
            </Button>
            <Button size="small" variant="contained" onClick={handleAddToGoldenSet} disabled={addingGolden}
              startIcon={addingGolden ? <CircularProgress size={12} color="inherit" /> : <GoldenSetIcon sx={{ fontSize: 14 }} />}
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              {addingGolden ? 'Adding…' : 'Add to Golden Set'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// ── Golden Set shortcut dialog ────────────────────────────────────────────────

interface GoldenSetDialogProps {
  item: HITLReviewItem | null;
  clientId: string;
  onClose: () => void;
  onAdded: () => void;
}

const GoldenSetDialog: React.FC<GoldenSetDialogProps> = ({ item, clientId, onClose, onAdded }) => {
  const theme = useTheme();
  const [name, setName]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (item) setName(item.ticket?.title ?? `Session ${item.session.id.slice(0, 8)}`);
  }, [item]);

  const handleSave = async () => {
    if (!item) return;
    setSaving(true);
    setError(null);
    try {
      await PlaybooksService.createGoldenSetEntry({
        client_id: clientId,
        session_id: item.session.id,
        name: name.trim() || undefined,
      });
      onAdded();
      onClose();
    } catch (e: any) {
      setError(e.message ?? 'Failed to add to golden set');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Add to Golden Set</Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem' }}>
          Promote this session as a quality benchmark in AI Gym
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth size="small" label="Entry name"
          value={name} onChange={(e) => setName(e.target.value)}
          InputProps={{ sx: { fontSize: '0.82rem' } }}
          InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
        />
        {error && (
          <Typography variant="caption" sx={{ color: theme.palette.error.main, display: 'block', mt: 1, fontSize: '0.72rem' }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <GoldenSetIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: '0.75rem' }}>
          {saving ? 'Adding…' : 'Add to Golden Set'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Curate dialog (candidate → golden set, no prior verdict required) ────────

interface CurateDialogProps {
  sessionId: string;
  defaultName: string;
  clientId: string;
  onClose: () => void;
  onAdded: () => void;
}

const CurateDialog: React.FC<CurateDialogProps> = ({ sessionId, defaultName, clientId, onClose, onAdded }) => {
  const theme = useTheme();
  const [name, setName]     = useState(defaultName);
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await PlaybooksService.createGoldenSetEntry({
        client_id: clientId,
        session_id: sessionId,
        name: name.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onAdded();
    } catch (e: any) {
      setError(e.message ?? 'Failed to curate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <GoldenSetIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Curate into Golden Set</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem' }}>
          This session will become a benchmark used to score future AI playbook runs.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField fullWidth size="small" label="Entry name"
          value={name} onChange={(e) => setName(e.target.value)}
          InputProps={{ sx: { fontSize: '0.82rem' } }}
          InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
        />
        <TextField fullWidth size="small" label="Notes (optional)"
          multiline minRows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="What made this incident a good example?"
          InputProps={{ sx: { fontSize: '0.82rem' } }}
          InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
        />
        {error && (
          <Typography variant="caption" sx={{ color: theme.palette.error.main, fontSize: '0.72rem' }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <GoldenSetIcon sx={{ fontSize: 14 }} />}
          sx={{ fontWeight: 600, fontSize: '0.75rem',
            backgroundColor: theme.palette.warning.main,
            '&:hover': { backgroundColor: theme.palette.warning.dark } }}>
          {saving ? 'Curating…' : 'Curate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Session detail sidebar ────────────────────────────────────────────────────

const SessionSidebar: React.FC<{ item: HITLReviewItem }> = ({ item }) => {
  const theme = useTheme();
  const { session, ticket } = item;

  const queueAgeMs = Date.now() - new Date(session.created_at).getTime();
  const queueAgeDays = Math.floor(queueAgeMs / 86400000);
  const ageColor = queueAgeDays >= 3 ? theme.palette.error.main
    : queueAgeDays >= 1 ? theme.palette.warning.main
    : theme.palette.success.main;

  return (
    <Box sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 1.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, letterSpacing: '0.08em', fontSize: '0.65rem', textTransform: 'uppercase' }}>
        Session Detail
      </Typography>

      {/* Queue age */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <ClockIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>Queue age</Typography>
        </Box>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 700, color: ageColor }}>
          {getRelativeTimeString(session.created_at)}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
          Created {formatDate(session.created_at)}
        </Typography>
      </Box>

      <Divider />

      {/* Assignee */}
      {ticket?.assignee_name && (
        <>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <PersonIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>Assigned to</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{ticket.assignee_name}</Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Reviewer (resolved sessions) */}
      {session.analyst_verdict_by_id && (
        <>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <PersonIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>Reviewed by</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
              {session.analyst_verdict_by_name ?? session.analyst_verdict_by_id}
            </Typography>
            {session.analyst_verdict_at && (
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
                {formatDate(session.analyst_verdict_at)}
              </Typography>
            )}
          </Box>
          <Divider />
        </>
      )}

      {/* HITL reason */}
      {session.requires_human_review && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <FlagIcon sx={{ fontSize: 13, color: theme.palette.error.main }} />
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase' }}>HITL reason</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, lineHeight: 1.5 }}>
            {session.hitl_reason ?? 'Flagged for human review'}
          </Typography>
        </Box>
      )}

      {/* Incident type */}
      {session.incident_type && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Incident type</Typography>
            <Chip label={session.incident_type.toUpperCase()} size="small"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700,
                backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main }} />
          </Box>
        </>
      )}
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const ReviewQueuePage: React.FC = () => {
  const theme    = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { clients }   = useSelector((state: RootState) => state.clients);
  const currentUser   = useSelector((state: RootState) => (state as any).auth?.user);

  type QueueFilter = 'all' | 'mine' | 'hitl' | 'resolved';
  type MainTab = 'review' | 'golden_set' | 'template_suggestions';

  const [clientId, setClientId]         = useState('');
  const [mainTab, setMainTab]           = useState<MainTab>('review');
  const [filter, setFilter]             = useState<QueueFilter>('all');
  const [queue, setQueue]               = useState<HITLReviewQueueResponse | null>(null);
  const [stats, setStats]               = useState<HITLReviewStats | null>(null);
  const [loading, setLoading]           = useState(false);
  const [candidates, setCandidates]     = useState<GoldenSetCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [suggestions, setSuggestions]           = useState<TemplateStepSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [analyzeLoading, setAnalyzeLoading]         = useState(false);
  const [verdictItem, setVerdictItem]   = useState<HITLReviewItem | null>(null);
  const [goldenItem, setGoldenItem]     = useState<HITLReviewItem | null>(null);
  const [curateTarget, setCurateTarget] = useState<GoldenSetCandidate | null>(null);
  const [sidebarItem, setSidebarItem]   = useState<HITLReviewItem | null>(null);
  const [toastMsg, setToastMsg]         = useState<string | null>(null);

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
  }, []);

  // cid === '' means "no client filter" — aggregate across every client the
  // caller can access, so the queue shows everything submitted for review
  // as soon as the page opens, without requiring a client to be picked first.
  const loadReview = useCallback(async (cid: string) => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        PlaybooksService.getReviewQueue(cid || undefined, {
          hitl_only: filter === 'hitl',
          reviewed:  filter === 'resolved',
        }),
        PlaybooksService.getReviewStats(cid || undefined),
      ]);
      setQueue(q);
      setStats(s);
    } catch {
      setQueue(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadCandidates = useCallback(async (cid: string) => {
    if (!cid) return;
    setCandidatesLoading(true);
    try {
      const res = await PlaybooksService.getGoldenSetCandidates(cid);
      setCandidates(res.candidates);
    } catch {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  const loadSuggestions = useCallback(async (cid: string) => {
    if (!cid) return;
    setSuggestionsLoading(true);
    try {
      const res = await PlaybooksService.getTemplateStepSuggestions(cid, 'pending');
      setSuggestions(res.suggestions);
    } catch {
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const handleAnalyze = async () => {
    if (!clientId) return;
    setAnalyzeLoading(true);
    try {
      await PlaybooksService.analyzeTemplateStepPatterns(clientId);
      setToastMsg('Analysis started — refresh in a moment to see new suggestions');
      setTimeout(() => setToastMsg(null), 4000);
      setTimeout(() => loadSuggestions(clientId), 3000);
    } catch {
      setToastMsg('Analysis failed — please try again');
      setTimeout(() => setToastMsg(null), 3000);
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const handleApproveSuggestion = async (id: string) => {
    try {
      await PlaybooksService.approveTemplateStepSuggestion(id);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      setToastMsg('Suggestion approved — template updated');
      setTimeout(() => setToastMsg(null), 3500);
    } catch (err: any) {
      setToastMsg(err.message || 'Failed to approve suggestion');
      setTimeout(() => setToastMsg(null), 3500);
    }
  };

  const handleRejectSuggestion = async (id: string) => {
    try {
      await PlaybooksService.rejectTemplateStepSuggestion(id);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      setToastMsg('Suggestion rejected');
      setTimeout(() => setToastMsg(null), 3000);
    } catch {
      setToastMsg('Failed to reject suggestion');
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  useEffect(() => {
    // "Needs Review" loads immediately, with or without a client picked —
    // clientId === '' aggregates across every accessible client. The other
    // two tabs still need one client selected first.
    if (mainTab === 'review') {
      loadReview(clientId);
      return;
    }
    if (!clientId) { setCandidates([]); setSuggestions([]); return; }
    if (mainTab === 'golden_set')           loadCandidates(clientId);
    if (mainTab === 'template_suggestions') loadSuggestions(clientId);
  }, [clientId, mainTab, filter]);

  const handleVerdict = async (verdict: 'correct' | 'incorrect' | 'partial', notes: string) => {
    if (!verdictItem) return;
    await PlaybooksService.setSessionVerdict(verdictItem.session.id, verdict, notes || undefined);
    setVerdictItem(null);
    if (sidebarItem?.session.id === verdictItem.session.id) setSidebarItem(null);
    const label = { correct: 'Correct', incorrect: 'Incorrect', partial: 'Partial' }[verdict];
    setToastMsg(`Verdict "${label}" submitted`);
    setTimeout(() => setToastMsg(null), 3500);
    if (clientId) loadReview(clientId);
  };

  // "Mine" is a client-side filter within pending views
  const visibleItems = (queue?.items ?? []).filter((item) => {
    if (filter !== 'mine') return true;
    return item.ticket?.assignee_id === currentUser?.id;
  });

  const cardSx = { backgroundColor: theme.palette.background.paper, borderRadius: 1.5, p: 2.5 };

  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, display: 'block', mb: 1.5, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
      {children}
    </Typography>
  );

  const isResolved = filter === 'resolved';

  const verdictColor = (v: string | null) => {
    if (v === 'correct')   return theme.palette.success.main;
    if (v === 'incorrect') return theme.palette.error.main;
    if (v === 'partial')   return theme.palette.warning.main;
    return theme.palette.text.disabled;
  };

  const columns: Column<HITLReviewItem>[] = [
    {
      key: 'session',
      header: isResolved ? 'Verdict' : 'HITL',
      minWidth: isResolved ? 80 : 50,
      align: 'center',
      render: (row) => isResolved ? (
        row.session.analyst_verdict ? (
          <Chip
            label={row.session.analyst_verdict.toUpperCase()}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
              color: verdictColor(row.session.analyst_verdict),
              backgroundColor: colorUtils.withOpacity(verdictColor(row.session.analyst_verdict), 0.1),
              border: `1px solid ${colorUtils.withOpacity(verdictColor(row.session.analyst_verdict), 0.3)}`,
            }}
          />
        ) : null
      ) : (
        row.session.requires_human_review ? (
          <Tooltip title={row.session.hitl_reason ?? 'Flagged for human review'}>
            <FlagIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
          </Tooltip>
        ) : null
      ),
    },
    ...(!clientId ? [{
      key: 'client',
      header: 'Client',
      minWidth: 120,
      align: 'left' as const,
      render: (row: HITLReviewItem) => (
        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
          {clients.find((c) => c.id === row.session.client_id)?.name ?? row.session.client_id.slice(0, 8) + '…'}
        </Typography>
      ),
    }] : []),
    {
      key: 'ticket',
      header: 'Ticket',
      minWidth: 200,
      align: 'left',
      render: (row) => row.ticket?.title ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem', color: theme.palette.text.primary }}>
            {row.ticket.title}
          </Typography>
          {row.ticket.assignee_name && (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
              {row.ticket.assignee_name}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>Alert-only session</Typography>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      minWidth: 80,
      align: 'left',
      render: (row) => row.ticket?.priority ? (
        <Chip label={row.ticket.priority.toUpperCase()} size="small"
          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.secondary }} />
      ) : null,
    },
    {
      key: 'incident_type',
      header: 'Type',
      minWidth: 90,
      align: 'left',
      render: (row) => row.session.incident_type ? (
        <Chip label={row.session.incident_type.toUpperCase()} size="small"
          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
            backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1),
            color: theme.palette.warning.main }} />
      ) : (
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>IOC-based</Typography>
      ),
    },
    {
      key: 'created_at',
      header: isResolved ? 'Reviewed' : 'In queue',
      minWidth: 110,
      align: 'left',
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.78rem' }}>
          {getRelativeTimeString(isResolved && row.session.analyst_verdict_at
            ? row.session.analyst_verdict_at
            : row.session.created_at)}
        </Typography>
      ),
    },
    {
      key: 'actions',
      header: '',
      minWidth: 160,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
          <Tooltip title="Open session">
            <IconButton size="small"
              onClick={(e) => { e.stopPropagation(); navigate(`/playbooks/sessions/${row.session.id}`); }}
              sx={{ color: theme.palette.text.disabled, '&:hover': { color: theme.palette.primary.main } }}>
              <OpenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          {isResolved ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); setGoldenItem(row); }}
              startIcon={<GoldenSetIcon sx={{ fontSize: 13 }} />}
              sx={{ height: 26, fontSize: '0.7rem', fontWeight: 600,
                borderColor: theme.palette.divider, color: theme.palette.text.secondary,
                '&:hover': { borderColor: theme.palette.warning.main, color: theme.palette.warning.main } }}
            >
              Golden Set
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); setVerdictItem(row); }}
              startIcon={<VerdictIcon sx={{ fontSize: 13 }} />}
              sx={{ height: 26, fontSize: '0.7rem', fontWeight: 600,
                borderColor: theme.palette.divider, color: theme.palette.text.secondary,
                '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main } }}
            >
              Verdict
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', width: '100%', display: 'flex', justifyContent: 'center', px: { xs: 2, sm: 3, md: 4 }, pt: 10, pb: 3 }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 4 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <VerdictIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
              <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Review Queue</Typography>
              {queue?.pending_hitl ? (
                <Chip label={`${queue.pending_hitl} HITL`} size="small" icon={<FlagIcon sx={{ fontSize: 11 }} />}
                  sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, color: theme.palette.error.main,
                    backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.1),
                    border: `1px solid ${colorUtils.withOpacity(theme.palette.error.main, 0.3)}`,
                    '& .MuiChip-icon': { color: theme.palette.error.main, ml: 0.75 } }} />
              ) : null}
            </Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              {mainTab === 'review'
                ? 'AI playbook sessions awaiting analyst verdict — HITL-flagged first'
                : mainTab === 'golden_set'
                ? 'Closed tickets ready to be curated as golden set benchmarks'
                : 'Playbook template improvements suggested from analyst behaviour patterns'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <ToggleButtonGroup size="small" exclusive
              value={mainTab}
              onChange={(_, v) => { if (v) { setMainTab(v as MainTab); setSidebarItem(null); } }}
              sx={{ '& .MuiToggleButton-root': { py: 0.4, px: 1.5, fontSize: '0.7rem', fontWeight: 600, height: 40, border: `1px solid ${theme.palette.divider}` } }}
            >
              <ToggleButton value="review">
                <VerdictIcon sx={{ fontSize: 12, mr: 0.5 }} />Needs Review
                {queue?.pending_hitl ? (
                  <Box component="span" sx={{ ml: 0.75, px: 0.6, py: 0.1, borderRadius: 0.5, fontSize: '0.6rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.15), color: theme.palette.error.main }}>
                    {queue.pending_hitl}
                  </Box>
                ) : null}
              </ToggleButton>
              <ToggleButton value="golden_set">
                <GoldenSetIcon sx={{ fontSize: 12, mr: 0.5 }} />Golden Set
                {candidates.length > 0 ? (
                  <Box component="span" sx={{ ml: 0.75, px: 0.6, py: 0.1, borderRadius: 0.5, fontSize: '0.6rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.15), color: theme.palette.warning.main }}>
                    {candidates.length}
                  </Box>
                ) : null}
              </ToggleButton>
              <ToggleButton value="template_suggestions">
                <SuggestionsIcon sx={{ fontSize: 12, mr: 0.5 }} />Template Suggestions
                {suggestions.length > 0 ? (
                  <Box component="span" sx={{ ml: 0.75, px: 0.6, py: 0.1, borderRadius: 0.5, fontSize: '0.6rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.info.main, 0.15), color: theme.palette.info.main }}>
                    {suggestions.length}
                  </Box>
                ) : null}
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small">
              <Select value={clientId} onChange={(e) => setClientId(e.target.value)} displayEmpty
                sx={{ height: 40, fontSize: '0.8rem', backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.primary, fontWeight: 500, minWidth: 180, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}>
                <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                  {mainTab === 'review' ? 'All clients' : 'Select client…'}
                </MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {mainTab === 'review' && (
              <ToggleButtonGroup size="small" exclusive
                value={filter}
                onChange={(_, v) => { if (v !== null) { setFilter(v as QueueFilter); setSidebarItem(null); } }}
                sx={{ '& .MuiToggleButton-root': { py: 0.4, px: 1.5, fontSize: '0.7rem', fontWeight: 600, height: 40, border: `1px solid ${theme.palette.divider}` } }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="mine">
                  <PersonIcon sx={{ fontSize: 12, mr: 0.5 }} />Mine
                </ToggleButton>
                <ToggleButton value="hitl">
                  <FlagIcon sx={{ fontSize: 12, mr: 0.5 }} />HITL
                </ToggleButton>
                <ToggleButton value="resolved">
                  <CorrectIcon sx={{ fontSize: 12, mr: 0.5 }} />Resolved
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {mainTab === 'template_suggestions' && (
              <Tooltip title="Analyze patterns now">
                <span>
                  <Button
                    disabled={!clientId || analyzeLoading}
                    onClick={handleAnalyze}
                    size="small"
                    startIcon={analyzeLoading ? <CircularProgress size={12} /> : <AnalyzeIcon sx={{ fontSize: 13 }} />}
                    sx={{ height: 40, fontSize: '0.7rem', fontWeight: 600, px: 1.5,
                      backgroundColor: theme.palette.background.tertiary,
                      color: theme.palette.info.main,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': { borderColor: theme.palette.info.main } }}
                  >
                    {analyzeLoading ? 'Analyzing…' : 'Analyze Now'}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <span>
                <IconButton disabled={mainTab !== 'review' && !clientId} onClick={() => {
                  if (mainTab === 'review') loadReview(clientId);
                  else if (clientId && mainTab === 'golden_set') loadCandidates(clientId);
                  else if (clientId) loadSuggestions(clientId);
                }}
                  sx={{ bgcolor: theme.palette.background.tertiary, color: theme.palette.info.main, borderRadius: 1, width: 40, height: 40,
                    '&:hover': { bgcolor: theme.palette.info.main, color: theme.palette.background.tertiary, transform: 'rotate(180deg)' },
                    transition: 'all 0.4s ease' }}>
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Needs Review tab ──────────────────────────────────────────── */}
        {mainTab === 'review' && stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Pending review',  value: stats.sessions_pending_review,        color: theme.palette.warning.main },
              { label: 'Coverage rate',   value: stats.review_coverage_rate != null ? `${Math.round(stats.review_coverage_rate * 100)}%` : '—', color: theme.palette.info.main },
              { label: 'Correct rate',    value: stats.verdict_correct_rate   != null ? `${Math.round(stats.verdict_correct_rate   * 100)}%` : '—', color: theme.palette.success.main },
              { label: 'HITL flagged',    value: stats.hitl_flagged,                   color: theme.palette.error.main },
            ].map(({ label, value, color }) => (
              <Grid key={label} size={{ xs: 6, sm: 3 }}>
                <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <SectionLabel>{label}</SectionLabel>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 700, fontFamily: 'monospace', lineHeight: 1, color }}>{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {mainTab === 'review' && stats && stats.sessions_reviewed > 0 && (
          <Box sx={{ ...cardSx, mb: 3 }}>
            <SectionLabel>Verdict distribution — {stats.sessions_reviewed} reviewed</SectionLabel>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {(['correct', 'incorrect', 'partial'] as const).map((v) => {
                const count = stats.verdict_distribution[v];
                const pct = stats.sessions_reviewed > 0 ? Math.round(count / stats.sessions_reviewed * 100) : 0;
                const color = v === 'correct' ? theme.palette.success.main : v === 'incorrect' ? theme.palette.error.main : theme.palette.warning.main;
                return (
                  <Box key={v} sx={{ flex: 1, minWidth: 100 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize', color: theme.palette.text.secondary }}>{v}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color }}>{pct}%</Typography>
                    </Box>
                    <Box sx={{ height: 4, borderRadius: 2, backgroundColor: theme.palette.background.tertiary }}>
                      <Box sx={{ height: '100%', borderRadius: 2, backgroundColor: color, width: `${pct}%`, transition: 'width 0.4s ease' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Golden Set tab ────────────────────────────────────────────── */}
        {mainTab === 'golden_set' && (
          <Box>
            {candidatesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
              </Box>
            ) : !clientId ? (
              <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>Select a client to view golden set candidates</Typography>
              </Box>
            ) : candidates.length === 0 ? (
              <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                <GoldenSetIcon sx={{ fontSize: 36, color: theme.palette.text.disabled, mb: 1.5, display: 'block', mx: 'auto' }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                  No candidates yet — close a ticket to make its session available here
                </Typography>
              </Box>
            ) : (
              <DataTable<GoldenSetCandidate>
                columns={[
                  {
                    key: 'incident_type',
                    header: 'Type',
                    minWidth: 90,
                    align: 'left',
                    render: (row) => row.incident_type ? (
                      <Chip label={row.incident_type.toUpperCase()} size="small"
                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                          backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1),
                          color: theme.palette.warning.main }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>IOC-based</Typography>
                    ),
                  },
                  {
                    key: 'ai_summary_preview',
                    header: 'Summary',
                    minWidth: 260,
                    align: 'left',
                    render: (row) => (
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 340 }}>
                        {row.ai_summary_preview || '—'}
                      </Typography>
                    ),
                  },
                  {
                    key: 'quality_score',
                    header: 'Quality',
                    minWidth: 80,
                    align: 'left',
                    render: (row) => {
                      const pct = Math.round((row.quality_score ?? 0) * 100);
                      const color = pct >= 80 ? theme.palette.success.main : pct >= 50 ? theme.palette.warning.main : theme.palette.error.main;
                      return (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'monospace', color }}>
                          {pct}%
                        </Typography>
                      );
                    },
                  },
                  {
                    key: 'steps_done',
                    header: 'Steps',
                    minWidth: 70,
                    align: 'left',
                    render: (row) => (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {row.steps_done}/{row.steps_total}
                      </Typography>
                    ),
                  },
                  {
                    key: 'created_at',
                    header: 'Closed',
                    minWidth: 110,
                    align: 'left',
                    render: (row) => (
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.78rem' }}>
                        {row.created_at ? getRelativeTimeString(row.created_at) : '—'}
                      </Typography>
                    ),
                  },
                  {
                    key: 'actions' as any,
                    header: '',
                    minWidth: 140,
                    align: 'right',
                    render: (row) => (
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                        <Tooltip title="Open session">
                          <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/playbooks/sessions/${row.session_id}`); }}
                            sx={{ color: theme.palette.text.disabled, '&:hover': { color: theme.palette.primary.main } }}>
                            <OpenIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" variant="outlined"
                          onClick={(e) => { e.stopPropagation(); setCurateTarget(row); }}
                          startIcon={<GoldenSetIcon sx={{ fontSize: 13 }} />}
                          sx={{ height: 26, fontSize: '0.7rem', fontWeight: 600,
                            borderColor: theme.palette.warning.main,
                            color: theme.palette.warning.main,
                            '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.08) } }}
                        >
                          Curate
                        </Button>
                      </Box>
                    ),
                  },
                ]}
                data={candidates}
                rowKey={(row) => row.session_id}
                activeRowKey={null}
                onRowClick={() => {}}
              />
            )}
          </Box>
        )}

        {/* ── Template Suggestions tab ─────────────────────────────────── */}
        {mainTab === 'template_suggestions' && (
          <Box>
            {suggestionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
              </Box>
            ) : !clientId ? (
              <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                  Select a client to view template suggestions
                </Typography>
              </Box>
            ) : suggestions.length === 0 ? (
              <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                <SuggestionsIcon sx={{ fontSize: 36, color: theme.palette.text.disabled, mb: 1.5, display: 'block', mx: 'auto' }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                  No pending suggestions — click "Analyze Now" to scan analyst behaviour patterns
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {suggestions.map((s) => {
                  const isAdd    = s.suggestion_type === 'add';
                  const tagColor = isAdd ? theme.palette.success.main : theme.palette.warning.main;
                  const action   = isAdd
                    ? s.step_data?.action
                    : s.evidence?.action;
                  const evidenceText = isAdd
                    ? `Added manually in ${s.evidence?.session_count ?? '?'} sessions`
                    : `Skipped in ${s.evidence?.skip_count ?? '?'} of ${s.evidence?.total ?? '?'} sessions (${Math.round((s.evidence?.skip_rate ?? 0) * 100)}%)`;

                  return (
                    <Box key={s.id} sx={{ ...cardSx, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {/* Left: tag + content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                          <Chip
                            label={isAdd ? 'ADD STEP' : 'REMOVE STEP'}
                            size="small"
                            icon={isAdd
                              ? <AddIcon sx={{ fontSize: 11 }} />
                              : <RemoveIcon sx={{ fontSize: 11 }} />}
                            sx={{
                              height: 20, fontSize: '0.6rem', fontWeight: 700,
                              color: tagColor,
                              backgroundColor: colorUtils.withOpacity(tagColor, 0.1),
                              border: `1px solid ${colorUtils.withOpacity(tagColor, 0.3)}`,
                              '& .MuiChip-icon': { color: tagColor, ml: 0.75 },
                            }}
                          />
                          <Chip
                            label={s.incident_type.toUpperCase()}
                            size="small"
                            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700,
                              backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1),
                              color: theme.palette.warning.main }}
                          />
                          {!isAdd && s.evidence?.phase && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem' }}>
                              {s.evidence.phase}
                            </Typography>
                          )}
                          {isAdd && s.step_data?.phase && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem' }}>
                              {s.step_data.phase}
                            </Typography>
                          )}
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.4 }}>
                          {action || '—'}
                        </Typography>

                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', display: 'block', mb: 0.5 }}>
                          {evidenceText}
                        </Typography>

                        {s.reason && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem', fontStyle: 'italic' }}>
                            {s.reason}
                          </Typography>
                        )}
                      </Box>

                      {/* Right: actions */}
                      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleApproveSuggestion(s.id)}
                          startIcon={<CorrectIcon sx={{ fontSize: 13 }} />}
                          sx={{ height: 30, fontSize: '0.7rem', fontWeight: 600,
                            borderColor: theme.palette.success.main,
                            color: theme.palette.success.main,
                            '&:hover': { backgroundColor: colorUtils.withOpacity(theme.palette.success.main, 0.08) } }}
                        >
                          Accept
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleRejectSuggestion(s.id)}
                          startIcon={<IncorrectIcon sx={{ fontSize: 13 }} />}
                          sx={{ height: 30, fontSize: '0.7rem', fontWeight: 600,
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.secondary,
                            '&:hover': { borderColor: theme.palette.error.main, color: theme.palette.error.main } }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ── Needs Review queue table + sidebar ────────────────────────── */}
        {mainTab === 'review' && <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
              </Box>
            ) : visibleItems.length === 0 ? (
              <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                  {filter === 'mine'     ? 'No sessions assigned to you' :
                   filter === 'hitl'     ? 'No HITL-flagged sessions pending' :
                   filter === 'resolved' ? 'No reviewed sessions yet' :
                   'Queue is empty — all sessions reviewed'}
                </Typography>
              </Box>
            ) : (
              <DataTable<HITLReviewItem>
                columns={columns}
                data={visibleItems}
                rowKey={(row) => row.session.id}
                activeRowKey={sidebarItem?.session.id ?? null}
                onRowClick={(row) => setSidebarItem(prev => prev?.session.id === row.session.id ? null : row)}
              />
            )}
          </Box>

          {/* Sidebar */}
          {sidebarItem && (
            <Box sx={{ width: 260, flexShrink: 0 }}>
              <SessionSidebar item={sidebarItem} />
            </Box>
          )}
        </Box>}

        {/* Dialogs */}
        <VerdictDialog
          item={verdictItem}
          clientId={clientId}
          onClose={() => setVerdictItem(null)}
          onSubmit={handleVerdict}
          onGoldenSetAdded={() => {
            setToastMsg('Added to Golden Set - open AI Gym to run benchmarks');
            setTimeout(() => setToastMsg(null), 4000);
          }}
        />
        <GoldenSetDialog
          item={goldenItem}
          clientId={clientId}
          onClose={() => setGoldenItem(null)}
          onAdded={() => {
            setGoldenItem(null);
            setToastMsg('Added to Golden Set - open AI Gym to run benchmarks');
            setTimeout(() => setToastMsg(null), 4000);
          }}
        />
        {/* Curate dialog for golden set tab */}
        {curateTarget && (
          <CurateDialog
            sessionId={curateTarget.session_id}
            defaultName={curateTarget.incident_type ? `${curateTarget.incident_type} — ${curateTarget.ai_summary_preview.slice(0, 60)}` : curateTarget.ai_summary_preview.slice(0, 80)}
            clientId={clientId}
            onClose={() => setCurateTarget(null)}
            onAdded={() => {
              setCurateTarget(null);
              setCandidates(prev => prev.filter(c => c.session_id !== curateTarget.session_id));
              setToastMsg('Curated into Golden Set - open AI Gym to run benchmarks');
              setTimeout(() => setToastMsg(null), 4000);
            }}
          />
        )}

        {/* Toast */}
        {toastMsg && (
          <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
            backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5, px: 2.5, py: 1.25, boxShadow: 6 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{toastMsg}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReviewQueuePage;
